import React, { useMemo, useState } from 'react'

import cn from 'classnames'

import ChainToken from '@src/components/ChainToken'
import ChevronDownIcon from '@src/components/icons/ChevronDown'
import { ChainId } from '@src/configs/chainInfo'
import SelectTokenModal from '@src/pages/Exchange/components/SelectTokenModal'
import { IExchangeData } from '@src/types/exchange'
import { IToken } from '@src/types/token'
import { useConfigStore } from '@src/zustand_stores'

interface Props {
    onChange: (__newData: {
        chainId?: number // Allow undefined
        token?: IToken // Allow undefined
        value?: bigint
    }) => void
    value: IExchangeData
    disabledChains?: number[]
    showNetwork?: boolean
    tokenIconSize?: number
    chainIconSize?: number
    buyData: IExchangeData
    sellData: IExchangeData
    side: 'buy' | 'sell'
    useChevronDown?: boolean
}

const TokenSelectionPill: React.FC<Props> = ({
    onChange,
    value,
    disabledChains,
    showNetwork,
    tokenIconSize = 40,
    chainIconSize = 16,
    buyData,
    sellData,
    side,
    useChevronDown = true,
}) => {
    const { chainId, token } = value

    const availableNetworks = useConfigStore(config => config.availableNetworks)

    const [isOpen, setIsOpen] = useState<boolean>(false)

    const onClickChain = () => {
        setIsOpen(prev => !prev)
    }

    const onClickTokenImage = () => {
        setIsOpen(prev => !prev)
    }

    const isDestinationSolana = useMemo(() => {
        return buyData.chainId === ChainId.Solana
    }, [buyData])

    const isOriginSolana = useMemo(() => {
        return sellData.chainId === ChainId.Solana
    }, [sellData])

    return (
        <div className='relative'>
            {showNetwork ? (
                <div
                    className={cn(
                        'flex h-7 cursor-pointer items-center space-x-2 rounded-md border-mach-button-secondary-border bg-mach-button-secondary-background px-[10px]',
                        isOpen ? 'border-mach-modal-border' : 'border-transparent'
                    )}
                    onClick={() => onClickChain()}
                >
                    <img src={availableNetworks[chainId]?.icon} alt='' className='h-[18px] w-[18px] rounded-md' />
                    <p className='font-normal text-mach-accent-forground'>{availableNetworks[chainId]?.name}</p>
                    <ChevronDownIcon className={cn('h-4 w-4 transition-all', isOpen ? 'rotate-180' : '')} />
                </div>
            ) : (
                <div
                    className={cn(
                        'flex cursor-pointer items-center',
                        isOpen ? 'border-mach-modal-border' : 'border-transparent'
                    )}
                    onClick={() => onClickTokenImage()}
                >
                    <div className='flex h-8 w-[50px] items-center md:h-10'>
                        <ChainToken
                            tokenLogo={token.name === 'BitTorrent' ? '/assets/token/btt.png' : token.logoURI}
                            networkIcon={token?.wrapped ? undefined : availableNetworks[chainId]?.icon}
                            tokenIconSize={tokenIconSize}
                            chainIconSize={chainIconSize}
                        />
                    </div>
                    <p className='max-w-[90px] truncate px-1 text-xl font-medium md:max-w-[110px] md:text-[32px]'>
                        {token?.symbol}
                    </p>
                    {useChevronDown ? <ChevronDownIcon /> : <></>}
                </div>
            )}

            <SelectTokenModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                selectedChain={chainId}
                setSelectedChain={__chainId => onChange({ chainId: +__chainId })}
                selectedToken={token}
                setSelectedToken={token => {
                    onChange({ token: token, chainId: token.chainId })
                }}
                availableChains={availableNetworks}
                disabledChains={disabledChains}
                isSolanaSwap={isDestinationSolana || isOriginSolana}
                side={side}
            />
        </div>
    )
}

export default TokenSelectionPill
